import moment from 'moment';

import { getIncidencePeriods, hasInvalidIncidence } from 'helpers/incidencePeriodHelpers';

export const getType = type => {
  switch (type) {
    case 'Polygon':
      return 'Zone';
    case 'Point':
      return 'Point';
    default:
      return 'Tronçon';
  }
};

/**
 * Generate extent sentence from 2 dates
 *
 * @param {string} dateFrom as MM-DD
 * @param {string} dateTo as MM-DD
 * @return {string} Date extent sentence
 */
export const formatIncidenceDateExtent = (dateFrom, dateTo) => {
  if (!dateFrom || !dateTo) {
    return '';
  }

  const year = moment().year();
  const fDateFrom = moment(`${year}-${dateFrom}`).format('DD MMM');
  const fDateTo = moment(`${year}-${dateTo}`).format('DD MMM');

  return ` du ${fDateFrom} au ${fDateTo}`;
};

/**
 * Generate map feature title
 *
 * @param {object} incidence Incidence informations
 * @param {number} id Index of element
 * @returns {string} Output message
 */
export const getMessage = ({ incidence, name, label }, id) => {
  const labelToDisplay = label || `${getType(name)} ${id + 1}`;
  const incidenceMsg = incidence && !Number.isNaN(parseFloat(incidence.GRIDCODE))
    ? ` : incidence ${incidence.GRIDCODE}`
    : '';

  return `${labelToDisplay}${incidenceMsg}`;
};

/**
 * Sort an array of features by timestamp
 *
 * @param {Array} features Input features array
 * @returns {Array} Input array sorted by timestamp property
 */
export const sortFeaturesByTimestamp = features => (
  features.sort((a, b) => a.properties.timestampCreatedAt > b.properties.timestampCreatedAt)
);

/**
 * Filter an array of features
 *
 * @param {Array} stateFeatures
 * @param {Array} activityAuthTypes
 * @param {Array} activityDates
 * @returns {Array} Filtered features
 */
export const filterFeatures = (stateFeatures, activityAuthTypes = [], activityDates = []) =>
  stateFeatures.reduce((features, feature) => {
    if (feature.properties.name !== 'LineString') {
      return [...features, { feature, incidence: feature.properties.incidence }];
    }

    // search for a parent LineString feature
    const parentPos = features.findIndex(({ feature: currFeature }) =>
      currFeature.properties.relatedFeatureId === feature.properties.relatedFeatureId);

    // Get the biggest incidence between selected periods
    const GRIDCODE = Object.keys(getIncidencePeriods(activityDates))
      .reduce((acc, curr) => Math.max(+feature.properties[curr], acc), 0);

    const incidence = { GRIDCODE };
    const warning = activityAuthTypes.some(warn => {
      const type = warn.replace(/chemins_?/, '');
      if (!warn.includes('AUT') || feature.properties[type] === undefined) {
        return false;
      }
      return feature.properties[type] === 0;
    });

    if (parentPos > -1) {
      const prevFeature = features[parentPos];

      if (GRIDCODE > prevFeature.incidence.GRIDCODE) {
        prevFeature.incidence = { GRIDCODE };
      }

      if (!activityDates.length) {
        prevFeature.incidence = { GRIDCODE: 0 };
      }

      prevFeature.warning = warning || prevFeature.warning;

      return features;
    }

    return [...features, { feature, incidence, warning }];
  }, []);

/**
 * Get the index of a feature from an array of features
 *
 * @param {*} feature The feature we are getting index
 * @param {*} features Full features search list
 * @returns {number} Feature index in features
 */
export const getFeatureIndex = (feature, features) =>
  features.findIndex(({ properties: propsB }) => {
    if (feature.properties.relatedFeatureId) {
      return propsB.relatedFeatureId === feature.properties.relatedFeatureId;
    }
    return propsB.id === feature.properties.id;
  });

/**
 * Add a tooltip property to each feature in array
 *
 * @param {Array} features Array of features to process
 */
export const addTooltipProperties = features => {
  const sortedFeatures = sortFeaturesByTimestamp(features);
  const filteredFeatures = filterFeatures(sortedFeatures).map(({ feature }) => feature);

  return features.map(featureA => ({
    ...featureA,
    properties: {
      ...featureA.properties,
      tooltip: getMessage(
        featureA.properties,
        getFeatureIndex(featureA, filteredFeatures),
      ),
    },
  }));
};

export function validateFeature (features, activity) {
  const { type, eventDates } = activity;

  if (!type || !eventDates.length) {
    return false;
  }

  const activityFeatures = features.filter(({ properties: { activity: activityId } }) =>
    activityId === activity.uid);

  if (!activityFeatures.length) {
    return false;
  }

  return !hasInvalidIncidence(activityFeatures, eventDates);
}

/**
 * Add a label to a property feature
 *
 * @param {*} feature The feature we are getting index
 * @param {string} label
 * @returns {object} Feature with the new label
 */
export const withLabelProperty = (feature = {}, label) => ({
  ...feature,
  properties: {
    ...feature.properties,
    label,
  },
});


/**
 * Compare two features and check if they have a relation
 *
 * @param {Object} feature one
 * @param {Object} feature two
 * @returns {boolean} both relation features match
 */
export const areMatching = (
  { id: idA, properties: { relatedFeatureId: relatedA } },
  { id: idB, properties: { relatedFeatureId: relatedB } },
) => {
  const areSameFeature = idA === idB;
  const haveSameRelated = relatedA && relatedA === relatedB;

  return areSameFeature || haveSameRelated;
};



// WEBPACK FOOTER //
// ./src/helpers/featuresHelpers.js